'use client'

import React, { FC } from 'react'
import { Info, ShieldCheck } from 'lucide-react'
import { isSnipeProtectedListing } from '@/utils/market/secure-purchase'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'

export const SNIPE_PROTECTION_LABEL = 'Snipe Protection'
/** The explainer page the (i) in the tooltip opens in a new tab. */
export const SNIPE_PROTECTION_LEARN_URL = '/learn/snipe-protection'

/**
 * The one snipe-protection shield. Green, with a "Snipe Protection" tooltip on
 * hover, optionally followed by a short label ("On"). Every surface that marks
 * protection (listing cards, activity rows, invoices, history) renders this so
 * the icon, colour and hover behave the same everywhere.
 */
export const SnipeShield: FC<{ className?: string; iconClassName?: string; label?: string }> = ({
	className,
	iconClassName,
	label
}) => (
	<TooltipProvider delayDuration={150}>
		<Tooltip>
			<TooltipTrigger asChild>
				<span
					className={cn(
						'inline-flex w-fit shrink-0 items-center gap-1',
						className,
						'text-emerald-700 dark:text-emerald-400'
					)}
					aria-label={SNIPE_PROTECTION_LABEL}
				>
					<ShieldCheck className={cn('size-3.5 shrink-0', iconClassName)} />
					{label}
				</span>
			</TooltipTrigger>
			<TooltipContent side="top" sideOffset={4} className="px-2 py-1 text-xs">
				<span className="inline-flex items-center gap-1.5">
					{SNIPE_PROTECTION_LABEL}
					<a
						href={SNIPE_PROTECTION_LEARN_URL}
						target="_blank"
						rel="noopener noreferrer"
						aria-label="Learn how snipe protection works (opens in a new tab)"
						className="inline-flex items-center text-muted-foreground hover:text-foreground"
						onClick={(e) => e.stopPropagation()}
					>
						<Info className="size-3" />
					</a>
				</span>
			</TooltipContent>
		</Tooltip>
	</TooltipProvider>
)

/**
 * Listings that settle through the private miner relay (NAT today, more
 * collections as the relay rolls out). Purchase routing never reads this —
 * it is display only, unlike `isSnipeProtectedListing`.
 */
export function isPrivateRelayListing(item: unknown): boolean {
	if (!item || typeof item !== 'object') return false
	const record = item as Record<string, unknown>
	const escrow =
		record.escrow && typeof record.escrow === 'object' ? (record.escrow as Record<string, unknown>) : record
	return escrow.private_relay === true
}

/** Shield on listings that settle with snipe protection or through the private relay. */
const SecureListingBadge: FC<{ listing?: unknown; className?: string; iconClassName?: string }> = ({
	listing,
	className,
	iconClassName
}) => {
	if (!isSnipeProtectedListing(listing) && !isPrivateRelayListing(listing)) {
		return null
	}
	return <SnipeShield className={className} iconClassName={iconClassName} />
}

export default SecureListingBadge
